import Axios, {
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
} from 'axios';

import { getFromLocalStorage } from '@/lib/utils';
import {
  AUTH_LOCAL_STORAGE_KEYS,
  PROJECT_LOCAL_STORAGE_KEYS,
} from '@/constants/localStorage';
import { env } from '@/config/env';

export type ApiBackend = 'upstream' | 'landing';

let isHandlingUnauthorized = false;

const stripTrailingSlashes = (value: string) => value.replace(/\/+$/, '');

const ensureLeadingSlash = (value: string) =>
  value.startsWith('/') ? value : `/${value}`;

const clearAuthStorage = () => {
  localStorage.removeItem(AUTH_LOCAL_STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem(AUTH_LOCAL_STORAGE_KEYS.LANDING_SESSION_TOKEN);
  localStorage.removeItem(AUTH_LOCAL_STORAGE_KEYS.USER);
  localStorage.removeItem(AUTH_LOCAL_STORAGE_KEYS.BRAND_ID);
  localStorage.removeItem(AUTH_LOCAL_STORAGE_KEYS.TOKEN_EXPIRES_AT);
  localStorage.removeItem(PROJECT_LOCAL_STORAGE_KEYS.PROJECT_ID);
};

const notifyUnauthorized = () => {
  // Allow AuthContext (and anything else) to react immediately.
  // ProtectedRoute will redirect once auth state becomes unauthenticated.
  window.dispatchEvent(new Event('auth:unauthorized'));
};

Axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401 && !isHandlingUnauthorized) {
      isHandlingUnauthorized = true;
      try {
        clearAuthStorage();
        notifyUnauthorized();
      } finally {
        // Release the lock after a short window to collapse parallel 401 bursts.
        setTimeout(() => {
          isHandlingUnauthorized = false;
        }, 1000);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Entity "scope" used to target a backend wrapper endpoint
 * (`/wrap/:scope/entities/*`). The landing backend is responsible for mapping
 * the scope to the upstream collection/service identifiers, so these are no
 * longer provided by the frontend.
 */
export type ApiScope =
  | 'project'
  | 'component'
  | 'reusable_component'
  | 'module'
  | 'section'
  | 'interface'
  | 'element'
  | 'page_element'
  | 'entity'
  | 'enum_type';

export interface ApiQueryParams {
  // Sensitive upstream identifiers are injected by the landing backend and are
  // intentionally optional here (the frontend never needs to provide them).
  collection_key?: string;
  brand_service_id?: string;
  project_id?: string;
  base_type?: 'json' | 'toon';
  export_type?: 'json' | 'toon' | 'csv' | 'excel' | 'pdf' | 'toon';
}

export interface AxiosRequestOptions<D> extends AxiosRequestConfig<D> {
  // params can include ApiQueryParams plus additional query params (e.g., fk[index][field])
  params?: ApiQueryParams &
  Record<string, string | number | boolean | undefined>;
  excludeAuthentication?: boolean;
  headers?: AxiosRequestHeaders;
  backend?: ApiBackend;
  /**
   * When set, the request is routed to the landing backend wrapper endpoint for
   * this entity scope. The upstream `/entities/...` path is rewritten to
   * `/wrap/<scope>/entities/...` and `backend` is forced to `'landing'`.
   */
  scope?: ApiScope;
  /** API version segment in the path, e.g. "v2.0.0" (default) or "v1". */
  apiVersion?: string;
  masterModule?: boolean;
  smoothSwipe?: boolean;
  internetCash?: boolean;
  middlewareModule?: boolean;
}

export async function apiRequest<D = object, R = unknown>({
  url,
  method,
  data,
  headers = {} as AxiosRequestHeaders, // Default to empty object
  params,
  responseType,
  excludeAuthentication = false,
  backend = 'upstream',
  scope,
  apiVersion = 'v2.0.0',
  ...restConfig
}: // middlewareModule = false,
  // masterModule = false,
  // smoothSwipe = false,
  // internetCash = false,
  AxiosRequestOptions<D>) {
  // A scope always routes through the landing backend wrapper endpoints.
  const effectiveBackend: ApiBackend = scope ? 'landing' : backend;
  const resolvedUrl = scope
    ? (url || '').replace(/^\/entities(?=\/|$)/, `/wrap/${scope}/entities`)
    : url || '';

  const token = excludeAuthentication
    ? null
    : effectiveBackend === 'landing'
      ? getFromLocalStorage(AUTH_LOCAL_STORAGE_KEYS.LANDING_SESSION_TOKEN)
      : getFromLocalStorage(AUTH_LOCAL_STORAGE_KEYS.AUTH_TOKEN);

  // Merge headers: prioritize caller headers, add Authorization when needed.
  const mergedHeaders = {
    ...headers,
    ...(token && !headers.Authorization
      ? { Authorization: `Bearer ${token}` }
      : {}),
  };

  const apiOrigin = stripTrailingSlashes(
    effectiveBackend === 'landing' ? env.LANDING_API_URL : env.API_URL
  );
  const baseUrl = `${apiOrigin}/api/${apiVersion}`;
  const requestUrl = `${baseUrl}${ensureLeadingSlash(resolvedUrl)}`;

  // Choose base URL based on mastermodule and smoothSwipe flags
  // const baseUrl = baseUrlMap.default;

  return await Axios.request<D, AxiosResponse<R>>({
    ...restConfig,
    url: requestUrl,
    method,
    data,
    headers: mergedHeaders,
    params,
    responseType,
  });
}
